import { useEffect, useState } from "react";
import Icon from "../../../../components/base/Icon";
import { API_ROUTES } from "../../../../core/Constants";
import { useHub } from "../../../../core/Global";


interface StateResult {
    reachable?: boolean
    // Raw stateInstance from Green API.
    state?: string
    message?: string
}

/**
 * Live Green API state for one row. Stored credentials only prove what root typed in, not that
 * the instance still exists over there -- an instance can be logged out, revoked, or run out of
 * quota without anything in our database changing. So this asks Green API on every page load
 * rather than showing a status we saved.
 */
export default function WaInstanceStateCell(data: any) {

    const hub = useHub();

    const [loading, loadingSet] = useState<boolean>(true);
    const [result, resultSet] = useState<StateResult>();

    useEffect(() => {
        if (!data.id) return;

        let cancelled = false;

        loadingSet(true);
        hub.Get(API_ROUTES.WaInstances + "state/" + data.id, res => {
            // The row can be gone by the time Green API answers -- a toggle refreshes the table.
            if (cancelled) return;
            resultSet(res.success ? res.data : { reachable: false, message: res.message });
            loadingSet(false);
        }, () => {
            // hub.Get skips the success callback on a transport error, so without this the cell
            // would spin forever instead of admitting it could not check.
            if (cancelled) return;
            resultSet({ reachable: false, message: "Could not reach the server." });
            loadingSet(false);
        });

        return () => { cancelled = true; };
    }, [data.id])

    if (loading) {
        return (
            <div className="fs-8 text-gray-600">
                <Icon icon="spinner-third" spin type="solid" size={12} className="me-2" />
                Checking...
            </div>
        )
    }

    if (!result?.reachable) {
        return (
            <div className="fs-7">
                <span className="badge badge-light-danger">Unavailable</span>
                {
                    result?.message &&
                    <div className="fs-8 text-gray-600 mt-1">{result.message}</div>
                }
            </div>
        )
    }

    // Green API's own vocabulary, mapped to something readable. Anything unrecognised is shown
    // verbatim rather than swallowed, so a new state does not silently read as fine.
    const state = result.state ?? "";
    const known: { [key: string]: { label: string, badge: string } } = {
        authorized: { label: "Authorized", badge: "badge-light-success" },
        notAuthorized: { label: "Not Authorized", badge: "badge-light-danger" },
        blocked: { label: "Blocked", badge: "badge-light-danger" },
        sleepMode: { label: "Sleep Mode", badge: "badge-light-warning" },
        starting: { label: "Starting", badge: "badge-light-warning" },
        yellowCard: { label: "Yellow Card", badge: "badge-light-warning" },
    };

    const shown = known[state] ?? { label: state, badge: "badge-light-warning" };

    return (
        <div className="fs-7">
            <span className={"badge " + shown.badge}>{shown.label}</span>
        </div>
    )
}
